import { useState } from 'react';
import styled from 'styled-components';

import { colors, effects } from '/css';
import Icon from '/components/ui/Icon';
import LoadingCircle from '/components/ui/LoadingCircle';
import bytesToSize from '/lib/utils/bytesToSize';
import SlideShow from '/components/ui/SlideShow';

export default function FileList({
    files,
    onRemove = undefined,
    grid = false,
    lightbox = false,
    onImageClick = (file) => {},
}) {
    const [lightboxData, setLightboxData] = useState(null);

    const windowWidth = () => {
        return typeof window === 'undefined'
            ? 0
            : window.innerWidth ||
                  document.documentElement.clientWidth ||
                  document.body.clientWidth;
    };

    const fileUrl = (file, width) => {
        const w = width || windowWidth() || 300;
        const url = file?.preview || file?.url || file?.uRL || file;
        if (!url) return '';
        return url + (~url.indexOf('.heic') ? `?w=${w}&fm=png` : `?w=${w}`);
    };

    const onImageClicked = (file, index) => {
        onImageClick(file);
        if (!lightbox) return;
        setLightboxData({
            images: (Array.isArray(files) ? files : []).map((i) => ({
                ...i,
                url: fileUrl(i),
            })),
            index,
        });
    };

    return (
        <FileListWrapper grid={grid} className="file-list">
            {lightboxData && (
                <SlideShow
                    images={lightboxData.images || files}
                    openAtIndex={lightboxData.index}
                    onClose={() => setLightboxData(null)}
                />
            )}

            {(Array.isArray(files) ? files : []).map((file, i) => {
                const name = String(file.name || file.originalFilename || file);
                const ext = name.split('.').pop();
                const isDocument = name.endsWith('pdf') || name.endsWith('k2o');
                return (
                    <li
                        key={`${
                            file.name || file.filename || file.originalFilename
                        }-${i}`}
                        className={`mb-24 file-item file-${i}`}
                    >
                        <div className="preview">
                            <div
                                className={[
                                    'image-container',
                                    'mr-24',
                                    file.processing ? 'loading' : 'loaded',
                                ].join(' ')}
                                onClick={() => onImageClicked(file, i)}
                                style={{
                                    backgroundImage: file.processing
                                        ? ''
                                        : `url(${fileUrl(file, 300)}`,
                                }}
                            >
                                {file.processing && (
                                    <LoadingCircle color={colors.grey6} />
                                )}
                                {!file.processing && isDocument && (
                                    <span className="ext-preview">{ext}</span>
                                )}
                            </div>
                            {!grid && file.name && file.size && (
                                <div className="image-details">
                                    <span className="name">
                                        {!isDocument ? 'Bilde' : 'Dokument'}{' '}
                                        {i + 1}
                                    </span>
                                    <span className="size">
                                        {bytesToSize(file.size)}
                                    </span>
                                </div>
                            )}
                        </div>
                        {!file.processing && !grid && onRemove && (
                            <Icon
                                icon="empty-field"
                                fill={colors.black}
                                onClick={() => onRemove(i)}
                            />
                        )}
                    </li>
                );
            })}
        </FileListWrapper>
    );
}

const FileListWrapper = styled.ul`
    display: flex;
    flex-wrap: wrap;
    ${(props) =>
        props.grid
            ? `
        margin-left:-9px;
        margin-right:-9px;
        margin-bottom:-20px;
        @media(max-width: 767px) {
            @media (min-width: 768px) and (max-width: 1024px) {
                margin-bottom:-16px;
            }
            @media(max-width: 767px) {
                margin-bottom:-12px;
            }
        }
    `
            : ``}

    .file-item {
        display: flex;
        flex-wrap: wrap;
        ${effects.shadow};
        padding: 14px 16px;
        border-radius: 12px;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        .preview {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            .ext-preview {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translateX(-50%) translateY(-50%);
                color: ${colors.grey6};
                font-size: 20px;
                text-transform: uppercase;
                font-family: 'gellix-bold';
            }
            .image-container {
                cursor: pointer;
                position: relative;
                width: 100px;
                height: 66px;
                background-position: center center;
                background-size: cover;
                border-radius: 12px;
                border: 1px solid ${colors.grey3};
                overflow: hidden;
                @media (max-width: 767px) {
                    width: 80px;
                    height: 56px;
                }
                > div {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translateX(-50%) translateY(-50%);
                }
            }
            .image-details {
                justify-content: center;
                display: flex;
                flex-direction: column;
                .name {
                    font-size: 18px;
                    font-family: 'gellix-semi';
                    color: ${colors.black};
                    margin-bottom: 8px;
                    @media (max-width: 767px) {
                        font-size: 16px;
                    }
                }
                .size {
                    font-size: 16px;
                    font-family: 'gellix-medium';
                    color: ${colors.grey6};
                    @media (max-width: 767px) {
                        font-size: 14px;
                    }
                }
            }
        }

        ${(props) =>
            props.grid
                ? `
            padding:0 9px;
            box-shadow: none;
            background:transparent;
            width: calc(25%);

            @media(max-width: 767px) {
                width: calc(50% - 9px);
            }

            .preview {
                width: 100%;
                .image-container {
                    cursor:pointer;
                    margin:0;
                    width:100%!important;
                    height: 0;
                    padding-top:66%;
                }
            }
        `
                : ``}
    }
`;
